import React, { useState, useEffect, useRef } from "react";
import { Container } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../slices/store";
import axiosInstance from 'src/common/axiosInstance';
import TopNav from "../../components/Common/TopNav";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import "./reports.css"
import { setSelectedExchangesData, resetVisibleExchangeIds, resetExchangeOrder } from "../../slices/Report/reducer";
import { resetCompareState } from "../../slices/Compare/reducer";
import { sortByExchangeOrder } from "src/helpers/commonHelper";
import ExchangeTable from "./ExchangeTable";
import TableCustomLoader from "src/components/Common/TableCustomLoader";
// Predefined icons for specific exchange names
const s3Bucket = 'unified-analytics-q1media';
const cdnBase = process.env.REACT_APP_CDN_URL
  ? `${process.env.REACT_APP_CDN_URL.replace(/\/$/, '')}/${s3Bucket}`
  : `https://${s3Bucket}.s3.us-east-1.amazonaws.com`;
const predefinedIcons = [
  { name: 'Overview', iconUrl: `${cdnBase}/Exchanges+Logo/overview-icon.svg` },
  { name: 'Programmatic', iconUrl: `${cdnBase}/Exchanges+Logo/programmatic-icon.svg` },
  { name: 'Other', iconUrl: `${cdnBase}/Exchanges+Logo/other-icon.svg` },
  { name: 'Audio', iconUrl: `${cdnBase}/Exchanges+Logo/audio-icon.svg` },
  { name: 'Device Id', iconUrl: `${cdnBase}/Exchanges+Logo/Device+ID.svg` },
];

/**
 * Main Reports Page Component
 * Displays exchange data tables and handles data fetching
 */
const ReportsPage = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [noDataMsg, setNoDataMsg] = useState("");
  const [campaignExchanges, setCampaignExchanges] = useState<any[]>([]);
  const [allSelectedExchanges, setAllSelectedExchanges] = useState<any[]>([]);
  const [campaignExchangesData, setCampaignExchangesData] = useState<any[]>([]);
  const lastErrorToastOrderRef = useRef<string | null>(null);
  const { visibleExchangeIds, exchangeOrder } = useSelector((state: RootState) => state.reports);
  const filters = useSelector((state: RootState) => state.filters);
  const {
    selectedOrder,
    dataBy,
    startDate,
    endDate,
  } = filters;

  // Reset compare state when leaving the Reports page
  useEffect(() => {
    return () => {
      dispatch(resetCompareState());
    };
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (!selectedOrder || selectedOrder === "") {
      setAllSelectedExchanges([]);
      dispatch(setSelectedExchangesData([]));
      // Start 5s timeout
      timer = setTimeout(() => {
        setLoading(false);
        setNoDataMsg("No campaign selected");
      }, 5000);
    } else {
      // If order is selected, ensure loading starts
      setLoading(true);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [selectedOrder]);

  // Fetch campaign exchanges when selectedOrder changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await fetchExchangeDetails();
      } catch (err) {
        console.error("Fetch error:", err);
        toast.error("Failed to load exchange data");
      } finally {
        setLoading(false);
      }
    };



    if (selectedOrder) {
      fetchData();
    }
  }, [selectedOrder]);


  /**
 * Fetches exchange details along with line items
 * @async
 */
  const fetchExchangeDetails = async () => {
    try {
      setLoading(true);
      // Reset all exchange-related states
      setCampaignExchanges([]);
      dispatch(setSelectedExchangesData([]));
      dispatch(resetVisibleExchangeIds());
      dispatch(resetExchangeOrder());
      setAllSelectedExchanges([]);
      setCampaignExchangesData([]);

      // API call to get exchange details
      const response = await axiosInstance.get(`/campaign-exchange-details`, {
        params: {
          orderId: selectedOrder,
          startDate: startDate,
          endDate: endDate
        },
      });

      const fetchedExchanges = response.data.data;

      // Handle empty response
      if (!fetchedExchanges || fetchedExchanges.length === 0) {
        // Only show toast if we haven't already shown it for this order
        if (lastErrorToastOrderRef.current !== selectedOrder) {
          toast.error("No exchanges found for the selected order.");
          lastErrorToastOrderRef.current = selectedOrder;
        }
        setCampaignExchanges([]);
        setAllSelectedExchanges([]);
        setCampaignExchangesData([]);
        dispatch(setSelectedExchangesData([]));
        setLoading(false)
        return;
      }

      //  Add dataBy to all exchanges
      const exchangesWithReportDataBy = fetchedExchanges.map((exchange: any) => ({
        ...exchange,
        dataBy: dataBy // Add dataBy to every exchange
      }));

      // Add icons to grouped exchanges based on predefined list
      const updatedExchanges = exchangesWithReportDataBy.map((exchange: any) => {
        const match = predefinedIcons.find(icon => icon.name === exchange.name);
        return match
          ? { ...exchange, iconUrl: match.iconUrl }
          : exchange;
      });

      setCampaignExchanges(updatedExchanges);

      // Reset error toast tracking since we successfully found exchanges
      lastErrorToastOrderRef.current = null;

      // Set default selection for exchanges
      const defaultSelectedExchanges = setDefaultSelection(updatedExchanges);
      setAllSelectedExchanges(defaultSelectedExchanges);
      dispatch(setSelectedExchangesData(defaultSelectedExchanges));

      setLoading(false);
    }
    catch {
      // Reset state on error
      setAllSelectedExchanges([]);
      dispatch(setSelectedExchangesData([]));
      toast.error("Failed to fetch exchange details");
      setLoading(false);
    }
  }

  /**
   * Sets default selection for exchanges (line items and metrics)
   * @param {Array} updatedExchanges - Array of exchange data
   * @returns {Array} - Array of exchanges with default selections
   */
  const setDefaultSelection = (updatedExchanges: any[] = []) => {
    return updatedExchanges.map(exchange => {
      // Select all line items by default
      const selectedLineItems = (exchange.lineItems || []).map((item: any) => item.Id);
      // Use defaultMetrics directly instead of frontend calculation
      if (exchange.selectedMetrics?.length > 0) {
        return {
          ...exchange,
          selectedLineItems,
          selectedMetrics: exchange.selectedMetrics,
          defaultMetrics: exchange.defaultMetrics || [],
        };
      } else {
        // If no selectedMetrics,  defaultMetrics as selectedMetrics
        return {
          ...exchange,
          selectedLineItems,
          selectedMetrics: exchange.defaultMetrics || [],
          defaultMetrics: exchange.defaultMetrics || []
        };
      }

    });
  };

  return (
    <React.Fragment>
      <Container fluid style={{ maxWidth: "100%" }}>
        <div className="page-content" style={{ paddingTop: "0" }}>
          <div className="mobile-breadcrumbs-section">
            <Breadcrumbs title="Dashboard" breadcrumbItem="Reports" titleLink="/dashboard" />
          </div>
          {/* Loading state */}
          {loading ? (
            <div className="exchange-card card"
              style={{
                padding: "40px", marginTop: "30px"
              }}>
              <TableCustomLoader length={2}
                message={selectedOrder == '' ? "Almost ready! waiting for campaign selection" : "Almost ready! Preparing campaign data for you."}
                spinnerIcon="✨" />
            </div>
          ) :
            // Render exchange tables if data exists
            allSelectedExchanges.length > 0 ? ((() => {
              const filteredExchanges = visibleExchangeIds.length > 0
                ? allSelectedExchanges.filter(ex => visibleExchangeIds.includes(ex.id))
                : allSelectedExchanges;

              // Apply custom order if set
              const displayedExchanges = sortByExchangeOrder(filteredExchanges, exchangeOrder);

              return displayedExchanges.map((exchange) => (
                <ExchangeTable
                  key={exchange.id}
                  individualExchangeData={exchange}
                  noOfExchanges={displayedExchanges.length}
                />
              ));
            })()
            )
              : (
                // No data available state
                <div className="exchange-card card" style={{ padding: "5vw" }}>
                  <div
                    className="d-flex justify-content-center align-items-center no-records"
                    style={{ minHeight: "250px", fontSize: "16px", borderRadius: "5px" }}
                  >{noDataMsg ? noDataMsg : "No data available"}</div>
                </div>)
          }


        </div>
      </Container>
    </React.Fragment >
  );
};

export default ReportsPage;
